import { createReducer } from '@reduxjs/toolkit';
import { interactiveCreate, interactiveStart } from '../../actions/actions.js';
import { ELEMENT_ACTION } from '../../actions/actionTypes.js';
import { setExtensionAllowed } from '#/store/slices/extensions/actions.js';

/**
 * @typedef {Object} ExtensionState
 * @property {string} name Identify extension, e.g. key, id
 * @property {boolean} allowed By user preferences via app > settings > interactive
 * @property {boolean} enabled For current object (video) via response from API
 * @property {boolean} consumable Loaded and ready to
 * @property {"idle"|"pending"|"fulfilled"|"rejected"} fetch Status of loading
 * @property {string} [src] URL to load extension where from
 * @property {any} [options] for extension, e.g. remote config via description from consul, reserved for future usage
 * @property {string} [version] of extension via consul description, reserved for future usage
 */

export default createReducer([], (builder) => {
  builder.addCase(setExtensionAllowed, (state, action) => {
    const {
      payload: { name, allowed },
    } = action;
    const extension = state.find((extension) => extension.name === name);
    if (extension) {
      extension.allowed = allowed;
    }
  });

  builder.addCase(interactiveCreate, (state, action)  =>{
    const {
      extensions = [],
    } = action.payload;

    return extensions.map((extension) => {
      const { enabled, status, options } = state.find(
        ({ name }) => name === extension.name,
      ) ?? {};
      extension.enabled = enabled;
      extension.options = {
        ...extension.options,
        ...options
      };

      return extension;
    });
  });

  builder.addCase(interactiveStart, (extensions, action)  => {
    const { payload } = action;

    extensions.forEach((extension) => {
      const extensionConfig = payload.config[extension.name] ?? {};
      const { enabled = false, ...videoOptions } = extensionConfig;

      Object.assign(extension.options, videoOptions);
      extension.enabled = enabled || extension.builtin;
    });
  });
  /**
	 * Update user preferences in run-time.
	 * @param extensions
	 * @param action
	 * @example action
	 {
			"type": "ELEMENT_ACTION",
			"data": {
				"event_type": "settings_changed",
				"name": "poll",
				"allowed": false
			}
		}
	 *
	 */
  builder.addCase(ELEMENT_ACTION, (extensions, action) => {
    const { event_type, name, allowed } = action.payload;

    if (event_type !== 'settings_changed') {
      return;
    }

    const indexOf = extensions.findIndex(
      (extension) => extension.name === name,
    );

    if (indexOf > -1) {
      extensions[indexOf].allowed = allowed;
    }
  });

  builder.addCase('update-extension-options', (extensions, action) => {
    const { name, options } = action.payload;

    const indexOf = extensions.findIndex(
      (extension) => extension.name === name,
    );

    if (indexOf > -1) {
      extensions[indexOf].options = {
        ...extensions[indexOf].options,
        ...options
      };
    }
  });
});
